import { ApolloError, useMutation, useQuery } from '@apollo/client';
import Routes from 'Routes';
import { useSnackbar } from '@myjobglasses/account-ui';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { hasCompanyAdminProfile, hasSchoolAdminProfile } from 'utils/user';
import {
  ComesFromEnum,
  Get_Current_UserQuery,
  Get_Current_UserQueryVariables,
  Sign_InMutation,
  Sign_InMutationVariables,
  Sign_UpMutation,
  Sign_UpMutationVariables,
  SigninErrorEnum,
  SignupErrorEnum,
} from '../../@types/graphql';
import { AuthenticationErrorTranslations } from './Authentication.translations';
import { GET_CURRENT_USER } from './gql/CurrentUser.gql';
import { SIGN_UP_MUTATION } from './gql/SignUp.gql';
import { SIGN_IN_MUTATION } from './gql/Signin.gql';
import useMarketplaceRedirect from 'hooks/use-marketplace-redirect';

type AuthParams = {
  email: string;
  password?: string;
  birthdate?: Date;
  highSchoolTeacher?: boolean;
  tutorEmail?: string;
  nonce?: string;
  invitationToken?: string;
  companyRegistrationToken?: string;
  comesFrom?: ComesFromEnum;
  vocationCoachSignup?: boolean;
  flow: 'signin' | 'signup';
  draftAttributionToken?: string;
};

export default function useAuth() {
  const navigate = useNavigate();
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { getRedirectUrl } = useMarketplaceRedirect();

  useQuery<Get_Current_UserQuery, Get_Current_UserQueryVariables>(GET_CURRENT_USER, {
    onCompleted: (data) => {
      if (data.currentUserV2) {
        const isCompanyHRAdmin =
          hasCompanyAdminProfile(data.currentUserV2) || data.currentUserV2.companyCreationRequest;
        const isSchoolHRAdmin = hasSchoolAdminProfile(data.currentUserV2);
        const isCoach = data.currentUserV2.isCoach;
        if (isCompanyHRAdmin || isCoach || isSchoolHRAdmin) {
          navigate(Routes.segmentation, { state: { isCompanyHRAdmin, isCoach, isSchoolHRAdmin } });
          return;
        }
        navigate(Routes.profileCompletion);
      }
    },
  });

  const [signup] = useMutation<Sign_UpMutation, Sign_UpMutationVariables>(SIGN_UP_MUTATION);

  const [signin] = useMutation<Sign_InMutation, Sign_InMutationVariables>(SIGN_IN_MUTATION);

  return async ({
    email,
    password,
    birthdate,
    tutorEmail,
    nonce,
    highSchoolTeacher,
    invitationToken,
    companyRegistrationToken,
    comesFrom,
    vocationCoachSignup,
    flow,
    draftAttributionToken,
  }: AuthParams) => {
    if (flow === 'signin' && password) {
      await signin({
        variables: {
          email,
          password,
          draftAttributionToken,
        },
      })
        .then(({ data }) => {
          if (!data?.signin) return;
          const isCompanyHRAdmin = hasCompanyAdminProfile(data.signin) || data.signin.companyCreationRequest;
          const isSchoolHRAdmin = hasSchoolAdminProfile(data.signin);
          const isCoach = data.signin.isCoach;
          if (isCompanyHRAdmin || isSchoolHRAdmin || isCoach) {
            navigate(Routes.segmentation, { state: { isCompanyHRAdmin, isCoach, isSchoolHRAdmin } });
            return;
          }
          localStorage.removeItem('completion_context'); // remove contact_ambassador completion context in case of users with two accounts
          window.location.href = getRedirectUrl();
        })
        .catch((error: ApolloError) => {
          const errorCodes = error.graphQLErrors.map(({ extensions }) => extensions?.code);

          switch (true) {
            case errorCodes.includes(SigninErrorEnum.NotFound):
              snackbar.error(intl.formatMessage(AuthenticationErrorTranslations.emailTaken));
              break;
            case errorCodes.includes(SigninErrorEnum.TutorEmailUnconfirmed):
              snackbar.info(intl.formatMessage(AuthenticationErrorTranslations.tutorEmailUnconfirmed));
              break;
            case errorCodes.includes(SigninErrorEnum.EmailUnconfirmed):
              snackbar.info(intl.formatMessage(AuthenticationErrorTranslations.emailUnconfirmed));
              break;
            default:
              break;
          }
        });
      return;
    }

    await signup({
      variables: {
        email,
        birthdate: birthdate?.toISOString(),
        highSchoolTeacher,
        tutorEmail,
        oauthNonce: nonce,
        invitationToken,
        companyRegistrationToken,
        comesFrom,
        vocationCoachSignup,
        password,
        draftAttributionToken,
      },
    })
      .then(() => {
        // Navigate to profile completion route and reload the page
        // since we are not calling the GET_PROFILE_COMPLETION query before
        // we cannot refetch it to update the state
        navigate(Routes.profileCompletion);
        navigate(0);
      })
      .catch((error: ApolloError) => {
        const errorCodes = error.graphQLErrors.map(({ extensions }) => extensions?.code);

        const emailBirthdatePasswordState = { state: { email, birthdate, password } };
        const emailState = { state: { email } };
        const tutorEmailState = {
          state: {
            email: tutorEmail,
            tutorEmail: true,
          },
        };

        switch (true) {
          case errorCodes.includes(SignupErrorEnum.EmailInvalid):
            snackbar.error(intl.formatMessage(AuthenticationErrorTranslations.emailInvalid));
            break;
          case errorCodes.includes(SignupErrorEnum.EmailForbiddenDomain):
            snackbar.error(intl.formatMessage(AuthenticationErrorTranslations.emailForbiddenDomain));
            break;
          case errorCodes.includes(SignupErrorEnum.TutorEmailBlank):
            navigate(Routes.mustProvideTutorEmail, emailBirthdatePasswordState);
            break;
          case errorCodes.includes(SignupErrorEnum.TutorEmailUnconfirmed):
            navigate(Routes.mustConfirmEmail, tutorEmailState);
            break;
          case errorCodes.includes(SignupErrorEnum.EmailUnconfirmed):
            navigate(Routes.mustConfirmEmail, emailState);
            break;
          case errorCodes.includes(SignupErrorEnum.BirthdateBlank):
            navigate(Routes.confirmSignup, emailState);
            break;
          case errorCodes.includes(SignupErrorEnum.BirthdateIsInvalid):
            snackbar.error(intl.formatMessage(AuthenticationErrorTranslations.cannotBeOlderThan));
            break;
          default:
            break;
        }
      });
  };
}
